import React, { createContext, useState, useEffect, useContext } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';

// Create context
const ServerLogContext = createContext(null);

// Maximum number of logs to keep in memory
const MAX_LOGS = 100;

export function ServerLogProvider({ children }) {
  const [logs, setLogs] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [socket, setSocket] = useState(null);
  const { isAuthenticated } = useAuth();

  // Initialize socket connection
  useEffect(() => {
    // Only connect if user is authenticated
    if (!isAuthenticated) {
      return;
    }

    // Always connect to port 5000 in development, or current port in production
    const protocol = window.location.protocol === 'https:' ? 'https://' : 'http://';
    const host = window.location.hostname;
    const port = process.env.NODE_ENV === 'production' ? window.location.port : '5000';

    // Create socket connection with explicit configuration
    const socketUrl = `${protocol}${host}${port ? `:${port}` : ''}`;
    console.log('Connecting to socket at:', socketUrl);

    const socketInstance = io(socketUrl, {
      transports: ['websocket', 'polling'],
      withCredentials: true
    });

    setSocket(socketInstance);

    socketInstance.on('connect', () => {
      setIsConnected(true);
    });

    socketInstance.on('connect_error', () => {
      setIsConnected(false);
    });

    socketInstance.on('disconnect', () => {
      setIsConnected(false);
    });

    socketInstance.on('server-log', (log) => {
      setLogs((prevLogs) => {
        const newLogs = [log, ...prevLogs.slice(0, MAX_LOGS - 1)];
        return newLogs;
      });
      
      // No longer auto-show popup when new logs arrive
      // The popup will only open when the user explicitly clicks on it
    });

    // Clean up on unmount
    return () => {
      socketInstance.disconnect();
    };
  }, [isAuthenticated]);

  // Toggle popup visibility
  const togglePopup = () => setShowPopup(!showPopup);

  // Clear logs
  const clearLogs = () => setLogs([]);

  return (
    <ServerLogContext.Provider value={{
      logs,
      showPopup,
      togglePopup,
      clearLogs,
      isConnected: isAuthenticated && isConnected,
      socket
    }}>
      {children}
    </ServerLogContext.Provider>
  );
}

// Custom hook to use the server log context
export function useServerLogs() {
  const context = useContext(ServerLogContext);
  if (!context) {
    throw new Error('useServerLogs must be used within a ServerLogProvider');
  }
  return context;
}
